import { supabase } from '../config/supabase'
import userService from './userService'

class AdminService {
  constructor() {
    // Admin emails for initial setup and fallback
    this.adminEmails = ['admin@logislip.com', 'support@logislip.com']
  }

  // Check if current user is admin
  isAdmin() {
    const user = userService.getCurrentUser()
    if (!user) return false
    
    // Primary check: user role
    if (user.role === 'admin') return true
    
    // Fallback check: admin email list (for backward compatibility)
    return this.adminEmails.includes(user.email.toLowerCase())
  }

  // Get dashboard statistics
  // Optional `days` window narrows revenue/new-user/recent lists; tier counts are always
  // current-state because they describe "right now", not a window.
  async getDashboardStats({ days = null } = {}) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      const windowStart = days
        ? new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
        : null

      // Current-state user counts (full table for accurate tier breakdown)
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, name, email, avatar_url, subscription_tier, subscription_status, created_at, usage_count, usage_limit')
        .order('created_at', { ascending: false })

      if (usersError) throw usersError

      // Payment data — joined with users so dashboard can show who paid.
      let paymentsQuery = supabase
        .from('payments')
        .select('id, amount, currency, status, billing_cycle, created_at, completed_at, plan_id, users:user_id(name, email)')
        .eq('status', 'completed')
        .order('created_at', { ascending: false })

      if (windowStart) {
        paymentsQuery = paymentsQuery.gte('created_at', windowStart)
      }

      const { data: payments, error: paymentsError } = await paymentsQuery
      if (paymentsError) throw paymentsError

      const totalUsers = users.length
      const freeUsers = users.filter(u => u.subscription_tier === 'free').length
      const paidUsers = users.filter(u => u.subscription_tier !== 'free' && u.subscription_status === 'active').length

      const totalRevenue = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)

      const currentMonth = new Date().getMonth()
      const currentYear = new Date().getFullYear()
      const thisMonthPayments = payments.filter(p => {
        const d = new Date(p.created_at)
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear
      })
      const monthlyRevenue = thisMonthPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)

      const last30Days = new Date()
      last30Days.setDate(last30Days.getDate() - 30)
      const newUsersLast30Days = users.filter(u => new Date(u.created_at) >= last30Days).length

      const newUsersInWindow = windowStart
        ? users.filter(u => new Date(u.created_at) >= new Date(windowStart)).length
        : newUsersLast30Days

      const subscriptionBreakdown = users.reduce((acc, user) => {
        const tier = user.subscription_tier || 'free'
        acc[tier] = (acc[tier] || 0) + 1
        return acc
      }, {})

      // Active paid subscriptions (tier !== free AND status === active).
      const activePaidSubscriptions = paidUsers

      const usersWithUsage = users.filter(u => u.usage_count > 0)
      const avgUsage = usersWithUsage.length > 0
        ? usersWithUsage.reduce((sum, u) => sum + u.usage_count, 0) / usersWithUsage.length
        : 0

      return {
        totalUsers,
        freeUsers,
        paidUsers,
        totalRevenue,
        monthlyRevenue,
        newUsersLast30Days,
        newUsersInWindow,
        subscriptionBreakdown,
        activePaidSubscriptions,
        avgUsage: Math.round(avgUsage * 100) / 100,
        recentUsers: users.slice(0, 10),
        recentPayments: payments.slice(0, 10),
        windowDays: days
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
      throw error
    }
  }

  // Get all users with pagination and filters
  async getUsers({ page = 1, limit = 50, search = '', tier = '', status = '' } = {}) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      let query = supabase
        .from('users')
        .select(`
          id, 
          name, 
          email, 
          avatar_url, 
          phone_number, 
          phone_verified,
          role,
          subscription_tier, 
          subscription_status, 
          subscription_end_date,
          usage_count, 
          usage_limit,
          created_at,
          last_login
        `, { count: 'exact' })

      // Apply search filter — strip characters that break PostgREST `or` filter syntax
      // (`,` separates filters; `()*\\` are reserved). Anything left is safe for ilike.
      if (search) {
        const safeSearch = String(search).replace(/[,()*\\]/g, '').trim()
        if (safeSearch) {
          query = query.or(`name.ilike.%${safeSearch}%,email.ilike.%${safeSearch}%`)
        }
      }

      // Apply tier filter
      if (tier) {
        query = query.eq('subscription_tier', tier)
      }

      // Apply status filter
      if (status) {
        query = query.eq('subscription_status', status)
      }

      // Apply pagination
      const offset = (page - 1) * limit
      query = query.range(offset, offset + limit - 1).order('created_at', { ascending: false })

      const { data: users, error, count } = await query

      if (error) throw error

      return {
        users,
        totalCount: count,
        currentPage: page,
        totalPages: Math.ceil(count / limit),
        hasNextPage: page * limit < count,
        hasPrevPage: page > 1
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      throw error
    }
  }

  // Detailed user view: profile + payments + recent audit/usage logs.
  async getUserDetails(userId) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      const [userRes, paymentsRes, logsRes] = await Promise.all([
        supabase.from('users').select('*').eq('id', userId).single(),
        supabase.from('payments')
          .select('id, plan_id, billing_cycle, amount, currency, status, payment_method, created_at, completed_at, razorpay_payment_id')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(50),
        supabase.from('usage_logs')
          .select('id, action, resource_type, resource_id, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(50)
      ])

      if (userRes.error) throw userRes.error
      if (paymentsRes.error) throw paymentsRes.error
      if (logsRes.error) throw logsRes.error

      return {
        user: userRes.data,
        payments: paymentsRes.data || [],
        usageLogs: logsRes.data || []
      }
    } catch (error) {
      console.error('Error fetching user details:', error)
      throw error
    }
  }

  // Update user subscription
  async updateUserSubscription(userId, subscriptionData) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      // Postgres rejects empty strings for TIMESTAMP / INTEGER columns
      // (error 22007). Coerce blanks to null and parse the numeric limit.
      const endDate = subscriptionData.endDate
      const normalizedEndDate = endDate === '' || endDate == null ? null : endDate

      const parsedUsageLimit = Number.isFinite(parseInt(subscriptionData.usageLimit, 10))
        ? parseInt(subscriptionData.usageLimit, 10)
        : null

      const updateData = {
        subscription_tier: subscriptionData.tier,
        subscription_status: subscriptionData.status,
        subscription_end_date: normalizedEndDate,
        updated_at: new Date().toISOString()
      }

      if (parsedUsageLimit !== null) {
        updateData.usage_limit = parsedUsageLimit
      }

      // Add role update if provided
      if (subscriptionData.role) {
        updateData.role = subscriptionData.role
      }

      const { data: updatedUser, error } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', userId)
        .select()
        .single()

      if (error) throw error

      // Log admin action
      await this.logAdminAction('user_subscription_updated', {
        userId,
        subscriptionData
      })

      return updatedUser
    } catch (error) {
      console.error('Error updating user subscription:', error)
      throw error
    }
  }

  // Delete user account
  async deleteUser(userId) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('id', userId)

      if (error) throw error

      // Log admin action
      await this.logAdminAction('user_deleted', { userId })

      return { success: true }
    } catch (error) {
      console.error('Error deleting user:', error)
      throw error
    }
  }

  // Get subscription plans — reads directly from the DB so the admin UI
  // always sees fresh data including inactive/draft plans.
  async getSubscriptionPlans({ includeInactive = true } = {}) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      let q = supabase
        .from('subscription_plans')
        .select('*')
        .order('sort_order', { ascending: true })

      if (!includeInactive) q = q.eq('active', true)

      const { data, error } = await q
      if (error) throw error

      // If the table is empty (e.g. migration hasn't been applied yet) fall
      // back to the in-memory defaults so the admin UI still renders.
      if (!data || data.length === 0) {
        const subscriptionService = (await import('./subscriptionService')).default
        return subscriptionService.getSubscriptionPlans()
      }

      const map = {}
      for (const row of data) {
        map[row.id] = {
          id: row.id,
          name: row.name,
          popular: !!row.popular,
          active: !!row.active,
          sortOrder: row.sort_order,
          features: Array.isArray(row.features) ? row.features : [],
          limitations: row.limitations || {},
          monthlyInvoiceLimit: row.monthly_invoice_limit,
          billing: row.billing || {},
          createdAt: row.created_at,
          updatedAt: row.updated_at
        }
      }
      return map
    } catch (error) {
      console.error('Error fetching subscription plans:', error)
      throw error
    }
  }

  // Create or update a plan. `plan.id` is the primary key (e.g. 'pro') and
  // is required. After a successful write the in-memory cache used by the
  // user-facing pricing UI is refreshed so the change is visible immediately.
  async upsertSubscriptionPlan(plan) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    if (!plan?.id || !/^[a-z0-9_-]+$/i.test(plan.id)) {
      throw new Error('Plan id must be alphanumeric (letters, digits, _ or -).')
    }
    if (!plan.name) {
      throw new Error('Plan name is required.')
    }

    const row = {
      id: plan.id,
      name: plan.name,
      popular: !!plan.popular,
      active: plan.active !== false,
      sort_order: Number.isFinite(plan.sortOrder) ? plan.sortOrder : 0,
      features: Array.isArray(plan.features) ? plan.features : [],
      limitations: plan.limitations || {},
      monthly_invoice_limit: plan.monthlyInvoiceLimit ?? null,
      billing: plan.billing || {}
    }

    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .upsert(row, { onConflict: 'id' })
        .select()
        .single()

      if (error) throw error

      await this.logAdminAction('subscription_plan_upserted', { planId: plan.id })

      try {
        const subscriptionService = (await import('./subscriptionService')).default
        await subscriptionService.refreshPlansFromDB()
      } catch (e) {
        console.warn('Plan saved but cache refresh failed:', e)
      }

      return data
    } catch (error) {
      console.error('Error upserting subscription plan:', error)
      throw error
    }
  }

  async deleteSubscriptionPlan(planId) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    // Refuse to delete a plan that still has active subscribers — we'd orphan
    // their billing rows. Admin should migrate them off first.
    const { count, error: countError } = await supabase
      .from('users')
      .select('id', { count: 'exact', head: true })
      .eq('subscription_tier', planId)
      .eq('subscription_status', 'active')

    if (countError) throw countError
    if (count && count > 0) {
      throw new Error(`Cannot delete: ${count} active user(s) are on this plan. Migrate them first.`)
    }

    const { error } = await supabase
      .from('subscription_plans')
      .delete()
      .eq('id', planId)

    if (error) throw error

    await this.logAdminAction('subscription_plan_deleted', { planId })

    try {
      const subscriptionService = (await import('./subscriptionService')).default
      await subscriptionService.refreshPlansFromDB()
    } catch (e) {
      console.warn('Plan deleted but cache refresh failed:', e)
    }

    return { success: true }
  }

  // Get payment history with analytics
  async getPayments({ page = 1, limit = 50, status = '', planId = '' } = {}) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      let query = supabase
        .from('payments')
        .select(`
          id,
          user_id,
          plan_id,
          billing_cycle,
          amount,
          currency,
          status,
          payment_method,
          created_at,
          completed_at,
          users!inner(name, email)
        `, { count: 'exact' })

      // Apply filters
      if (status) {
        query = query.eq('status', status)
      }

      if (planId) {
        query = query.eq('plan_id', planId)
      }

      // Apply pagination
      const offset = (page - 1) * limit
      query = query.range(offset, offset + limit - 1).order('created_at', { ascending: false })

      const { data: payments, error, count } = await query

      if (error) throw error

      return {
        payments,
        totalCount: count,
        currentPage: page,
        totalPages: Math.ceil(count / limit),
        hasNextPage: page * limit < count,
        hasPrevPage: page > 1
      }
    } catch (error) {
      console.error('Error fetching payments:', error)
      throw error
    }
  }

  // Get usage analytics. `days` narrows the usage_logs window.
  async getUsageAnalytics({ days = null } = {}) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      const windowStart = days
        ? new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
        : null

      let logsQuery = supabase
        .from('usage_logs')
        .select('action, resource_type, created_at, user_id')
        .order('created_at', { ascending: false })
        .limit(5000)

      if (windowStart) {
        logsQuery = logsQuery.gte('created_at', windowStart)
      }

      const { data: usageLogs, error: logsError } = await logsQuery

      if (logsError) throw logsError

      // Get resource usage data
      const { data: resourceUsage, error: resourceError } = await supabase
        .from('resource_usage')
        .select('user_id, resource_type, current_count, limit_count')

      if (resourceError) throw resourceError

      // Process analytics
      const actionCounts = usageLogs.reduce((acc, log) => {
        acc[log.action] = (acc[log.action] || 0) + 1
        return acc
      }, {})

      const resourceCounts = resourceUsage.reduce((acc, usage) => {
        if (!acc[usage.resource_type]) {
          acc[usage.resource_type] = { total: 0, used: 0 }
        }
        acc[usage.resource_type].total += usage.limit_count
        acc[usage.resource_type].used += usage.current_count
        return acc
      }, {})

      return {
        actionCounts,
        resourceCounts,
        totalActions: usageLogs.length,
        activeUsers: new Set(usageLogs.map(log => log.user_id)).size
      }
    } catch (error) {
      console.error('Error fetching usage analytics:', error)
      throw error
    }
  }

  // Log admin actions for audit trail
  async logAdminAction(action, details = {}) {
    const adminUser = userService.getCurrentUser()
    if (!adminUser) return

    try {
      await supabase
        .from('usage_logs')
        .insert({
          user_id: adminUser.id,
          action: `admin_${action}`,
          resource_type: 'admin',
          details: {
            ...details,
            admin_email: adminUser.email,
            timestamp: new Date().toISOString()
          }
        })
    } catch (error) {
      console.error('Error logging admin action:', error)
    }
  }

  // Bulk operations
  async bulkUpdateUserTier(userIds, newTier) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      const { data: updatedUsers, error } = await supabase
        .from('users')
        .update({
          subscription_tier: newTier,
          updated_at: new Date().toISOString()
        })
        .in('id', userIds)
        .select()

      if (error) throw error

      await this.logAdminAction('bulk_tier_update', {
        userIds,
        newTier,
        count: updatedUsers.length
      })

      return updatedUsers
    } catch (error) {
      console.error('Error bulk updating user tiers:', error)
      throw error
    }
  }

  // Update user role (promote/demote admin)
  async updateUserRole(userId, newRole) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    if (!['user', 'admin'].includes(newRole)) {
      throw new Error('Invalid role. Must be either "user" or "admin"')
    }

    try {
      const { data: updatedUser, error } = await supabase
        .from('users')
        .update({
          role: newRole,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId)
        .select()
        .single()

      if (error) throw error

      await this.logAdminAction('user_role_updated', {
        userId,
        newRole,
        previousRole: updatedUser.role
      })

      return updatedUser
    } catch (error) {
      console.error('Error updating user role:', error)
      throw error
    }
  }

  // System settings — admin-managed key/JSONB-value store.
  async getSystemSettings() {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }
    const { data, error } = await supabase.from('system_settings').select('key, value, updated_at, updated_by')
    if (error) throw error
    const map = {}
    for (const row of data || []) map[row.key] = row.value
    return { values: map, rows: data || [] }
  }

  async saveSystemSettings(updates) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }
    const rows = Object.entries(updates).map(([key, value]) => ({ key, value }))
    if (rows.length === 0) return { success: true }

    const { error } = await supabase.from('system_settings').upsert(rows, { onConflict: 'key' })
    if (error) throw error

    await this.logAdminAction('system_settings_updated', { keys: Object.keys(updates) })
    return { success: true }
  }

  // Audit log viewer — pull admin-prefixed actions from usage_logs.
  async getAdminAuditLogs({ page = 1, limit = 50 } = {}) {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    const offset = (page - 1) * limit
    const { data, error, count } = await supabase
      .from('usage_logs')
      .select('id, user_id, action, resource_type, resource_id, details, created_at, users:user_id(name, email)', { count: 'exact' })
      .like('action', 'admin_%')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1)

    if (error) throw error

    return {
      logs: data || [],
      totalCount: count || 0,
      currentPage: page,
      totalPages: Math.ceil((count || 0) / limit),
      hasNextPage: page * limit < (count || 0),
      hasPrevPage: page > 1
    }
  }

  // Export users data for analysis
  async exportUsersData() {
    if (!this.isAdmin()) {
      throw new Error('Unauthorized: Admin access required')
    }

    try {
      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      await this.logAdminAction('users_data_exported', {
        count: users.length
      })

      return users
    } catch (error) {
      console.error('Error exporting users data:', error)
      throw error
    }
  }
}

// Create singleton instance
const adminService = new AdminService()
export default adminService